import { directoryApi, type CreateResourceRequest } from '@my/directory-api';
import { hasEmail, hasEquipment, hasMobile, type ResourceImportDraft } from './types';

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}

/**
 * Create one resource. Self-contained and idempotent — a row that already
 * landed returns untouched, so "Retry failed" never double-creates. Errors are
 * swallowed into the returned draft so one bad row can't stop the batch.
 *
 * Fields the resource type doesn't allow are sent as `null`: the BE validator
 * applies type-conditional rules and would (correctly) reject a trailer with a
 * mobile number.
 */
export async function commitResourceDraft(
  organisationId: string,
  draft: ResourceImportDraft,
): Promise<ResourceImportDraft> {
  if (draft.status_ === 'created' && draft.createdResourceId) return draft;

  const body: CreateResourceRequest = {
    organisationId,
    type: draft.type,
    name: draft.name.trim(),
    displayName: draft.displayName.trim() || null,
    reference: draft.reference.trim() || null,
    mobileNumber: hasMobile(draft.type) ? draft.mobileNumber.trim() || null : null,
    email: hasEmail(draft.type) ? draft.email.trim() || null : null,
    organisationEquipmentTypeId: hasEquipment(draft.type)
      ? draft.organisationEquipmentTypeId || null
      : null,
    status: draft.status,
  };

  try {
    const created = await directoryApi.createResource(body);
    return { ...draft, status_: 'created', createdResourceId: created.id, error: undefined };
  } catch (err) {
    const message = errMsg(err);
    // An existing resource isn't a failure worth retrying — mark it skipped so
    // the operator can tell "already there" apart from "broken".
    if (/already exists|duplicate/i.test(message)) {
      return { ...draft, status_: 'skipped', error: message };
    }
    return { ...draft, status_: 'failed', error: message };
  }
}
